import React, { useEffect, useMemo } from 'react';
import StatusPill from './StatusPill';
import StatusBadge from './ui/StatusBadge';
import ExpandableSection from './ui/ExpandableSection';
import Timeline from './ui/Timeline';
import VerdictBanner from './ui/VerdictBanner';
import { formatDateTime } from '../utils/formatters';
import {
  describeApprovalRequirement,
  humanizePolicyId,
  humanizeProcessType,
  humanizeRoleId,
  resolveUserLabel,
} from '../utils/narration';
import { useDrawer } from './ui/drawer/DrawerContext';

const formatDateTimeOrDash = (value) => formatDateTime(value) || '—';

const ApprovalsList = ({ requiredRows, roles }) => {
  if (!requiredRows || requiredRows.length === 0) {
    return <p className="muted">No policy roles configured.</p>;
  }
  return (
  <div className="approvals-list">
    {requiredRows.map(({ role, approval }, index) => {
      const status = approval ? 'Signed' : 'Pending';
      const tone = approval ? 'success' : 'muted';
      const approverLabel = approval
        ? resolveUserLabel({
          userId: approval?.userId,
          email: approval?.approverLabel || approval?.email,
          walletAddress: approval?.wallet,
        })
        : 'Awaiting approver';
      const roleLabel = humanizeRoleId(role, roles);
      const approvedAt = approval ? formatDateTimeOrDash(approval.approvedAt) : '—';
      return (
        <div key={`${role}-${index}`} className="approval-item">
          <div className="approval-role">
            <span>{roleLabel}</span>
            <StatusBadge status={status} tone={tone} />
          </div>
          <div className="approval-meta">
            <span className="approval-role-label">{`Assigned: ${approverLabel}`}</span>
            <span>{approvedAt}</span>
          </div>
        </div>
      );
    })}
  </div>
  );
};

const ApprovalsTable = ({ requiredRows, roles }) => (
  <table className="approvals-table">
    <thead>
      <tr>
        <th>Role</th>
        <th>Approver</th>
        <th>Status</th>
        <th>Time</th>
      </tr>
    </thead>
    <tbody>
      {requiredRows.length === 0 && (
        <tr>
          <td colSpan="4" className="muted">No policy roles configured.</td>
        </tr>
      )}
      {requiredRows.map(({ role, approval }, index) => {
        const roleLabel = humanizeRoleId(role, roles);
        const approverLabel = approval
          ? resolveUserLabel({
            userId: approval?.userId,
            email: approval?.approverLabel || approval?.email,
            walletAddress: approval?.wallet,
          })
          : 'Awaiting approver';
        const status = approval ? 'Signed' : 'Pending';
        return (
          <tr key={`${role}-${index}`}>
            <td>{roleLabel}</td>
            <td>{approverLabel}</td>
            <td>
              <StatusBadge status={status} tone={approval ? 'success' : 'muted'} />
            </td>
            <td>{approval ? formatDateTimeOrDash(approval.approvedAt) : '—'}</td>
          </tr>
        );
      })}
    </tbody>
  </table>
);

const VerificationChecklist = ({ verification }) => {
  const renderRow = (label, statusValue) => {
    const status = statusValue || 'PENDING';
    const tone = status === 'PASS' ? 'success' : status === 'FAIL' ? 'danger' : 'muted';
    return (
      <div className="verification-row">
        <span>{label}</span>
        <StatusBadge status={status === 'PASS' ? 'Pass' : status === 'FAIL' ? 'Fail' : 'Pending'} tone={tone} />
      </div>
    );
  };

  return (
    <div className="verification-groups">
      <div className="verification-group">
        <div className="verification-title">Integrity checks</div>
        {renderRow('Request hash recorded', verification?.intentHashRecorded)}
        {renderRow('Approval signatures verified', verification?.approvalSignaturesVerified)}
      </div>
      <div className="verification-group">
        <div className="verification-title">Execution checks</div>
        {renderRow('Deployment execution recorded', verification?.executionHashRecorded)}
      </div>
      <div className="verification-group">
        <div className="verification-title">Evidence availability</div>
        {renderRow('CID retrievable', verification?.cidRetrievable)}
      </div>
    </div>
  );
};

const ReleaseDetails = ({ release, view, onViewChange, roles = [], policies = [] }) => {
  if (!release) return null;
  const { intent, policy, approvals, execution, derived } = release;
  const isEngineer = view === 'engineer';
  const { setDrawerData, openDrawer } = useDrawer();
  const overallStatus = derived?.overallStatus || 'PENDING_APPROVAL';
  const requiredRows = useMemo(() => {
    const roleQueues = new Map();
    approvals.forEach((approval) => {
      if (!approval.role) return;
      if (!roleQueues.has(approval.role)) {
        roleQueues.set(approval.role, []);
      }
      roleQueues.get(approval.role).push(approval);
    });
    return (policy.requiredRoles || []).map((role) => {
      const queue = roleQueues.get(role) || [];
      const approval = queue.shift() || null;
      roleQueues.set(role, queue);
      return { role, approval };
    });
  }, [approvals, policy.requiredRoles]);
  const approvalsRequired = derived?.approvalsRequired || policy?.requiredRoles?.length || 1;
  const timelineItems = useMemo(() => {
    const items = [];
    const createdAt = intent.createdAt || intent.created_at || null;
    const requester = intent.requesterEmail || intent.requestedByEmail || intent.requester || intent.createdByEmail || 'Requestor';
    if (createdAt) {
      items.push({
        key: `created-${createdAt}`,
        title: `Deployment request created by ${requester}`,
        time: formatDateTimeOrDash(createdAt),
        timestamp: new Date(createdAt).getTime()
      });
    }
    if (approvalsRequired > 0 && approvals.length === 0) {
      items.push({
        key: 'approval-requested',
        title: `Approval requested (${approvalsRequired} required)`,
        time: createdAt ? formatDateTimeOrDash(createdAt) : '',
        timestamp: createdAt ? new Date(createdAt).getTime() : 0
      });
    }
    approvals.forEach((approval, index) => {
      const resolvedActor = approval
        ? resolveUserLabel({
          userId: approval?.userId,
          email: approval?.approverLabel || approval?.email,
          walletAddress: approval?.wallet,
        })
        : 'Approver';
      const roleLabel = humanizeRoleId(approval?.role, []);
      const approverLabel = resolvedActor === 'Council member' || resolvedActor === 'Unknown actor'
        ? roleLabel
        : resolvedActor;
      const approvedAt = approval?.approvedAt || approval?.approved_at || null;
      items.push({
        key: `approved-${approval?.wallet || index}`,
        title: `Approval signed by ${approverLabel}`,
        time: formatDateTimeOrDash(approvedAt),
        timestamp: approvedAt ? new Date(approvedAt).getTime() : 0
      });
    });
    const executedAt = execution?.executedAt || execution?.executed_at;
    if (executedAt) {
      items.push({
        key: `executed-${executedAt}`,
        title: 'Deployment execution recorded',
        time: formatDateTimeOrDash(executedAt),
        timestamp: new Date(executedAt).getTime()
      });
    }
    const filtered = items.filter((item) => item.title && item.title.toLowerCase() !== 'intent');
    const sorted = filtered.sort((a, b) => a.timestamp - b.timestamp);
    return sorted.length > 0 ? sorted : [{ key: 'none', title: 'No timeline events yet', time: '' }];
  }, [
    approvals,
    approvalsRequired,
    execution?.executedAt,
    execution?.executed_at,
    intent.createdAt,
    intent.created_at,
    intent.requester,
    intent.requesterEmail,
    intent.requestedByEmail,
    intent.createdByEmail
  ]);
  const policyId = policy?.id || policy?.policyId || intent?.policy_id || intent?.policyId || '';
  const policyRecord = policies.find((entry) => entry.id === policyId);
  const policyRoles = policy?.requiredRoles
    || policy?.requiredRoleIds
    || intent?.required_roles
    || intent?.requiredRoles
    || policyRecord?.requiredRoles
    || policyRecord?.requiredRoleIds
    || [];
  const policyLabel = policy?.display || policyRecord?.name || humanizePolicyId(policyId, policies);
  const policyIntent = describeApprovalRequirement(policyRoles, roles);
  const expiryNote = (() => {
    if (!intent.expiresAt) return 'Not configured';
    return overallStatus === 'EXPIRED' ? 'Expired before completion' : 'Within expiry window';
  })();
  const expiryCountdown = (() => {
    if (!intent.expiresAt) return '';
    const expiresAt = new Date(intent.expiresAt).getTime();
    if (Number.isNaN(expiresAt)) return '';
    const diffMs = expiresAt - Date.now();
    if (diffMs <= 0) return 'Expired';
    const hours = Math.floor(diffMs / 3600000);
    const minutes = Math.floor((diffMs % 3600000) / 60000);
    if (hours > 0) return `~${hours}h ${minutes}m remaining`;
    return `~${minutes}m remaining`;
  })();
  const executionSummary = (() => {
    if (!execution?.status || execution.status === 'NOT_EXECUTED') return 'Not recorded';
    const label = execution.status === 'SUCCEEDED' ? 'Succeeded' : execution.status === 'FAILED' ? 'Failed' : execution.status;
    return `Deployment executed (${label})`;
  })();
  const authorizationTone = derived.approvalsSatisfied ? 'success' : 'warning';
  const executionTone = execution?.status && execution.status !== 'NOT_EXECUTED' ? 'success' : 'muted';
  const verdict = (() => {
    if (overallStatus === 'EXECUTED_SUCCESS' || overallStatus === 'APPROVED_PENDING_EXECUTION') {
      return { label: 'Approved & Executed', tone: 'success' };
    }
    if (overallStatus === 'EXECUTED_FAILED' || overallStatus === 'EXPIRED') {
      return { label: 'Expired / Failed', tone: 'danger' };
    }
    return { label: 'Awaiting approvals', tone: 'warning' };
  })();
  const complianceSentence = (() => {
    if (overallStatus === 'PENDING_APPROVAL') {
      return 'Execution blocked because required approvals are missing.';
    }
    if (overallStatus === 'APPROVED_PENDING_EXECUTION') {
      return 'Approvals are satisfied; deployment execution is pending.';
    }
    if (overallStatus === 'EXECUTED_SUCCESS') {
      return 'Deployment execution was recorded successfully.';
    }
    if (overallStatus === 'EXECUTED_FAILED') {
      return 'Deployment execution failed; see evidence for details.';
    }
    if (overallStatus === 'EXPIRED') {
      return 'Approval request expired before completion.';
    }
    return 'Approval state is pending review.';
  })();

  useEffect(() => {
    const timelineEvents = timelineItems
      .filter((item) => item && item.title && item.title.toLowerCase() !== 'intent')
      .map((item, index) => ({
        id: item.key || `${item.title}-${index}`,
        label: item.time ? `${item.title} · ${item.time}` : item.title
      }));
    const txHash = execution?.executionTxHash || execution?.txHash || execution?.executionHash || '';
    const auditItems = [
      { id: 'policy', label: `policyId: ${policy?.id || policy?.policyId || '—'}` },
      { id: 'roles', label: `roleIds: ${(policy?.requiredRoles || []).join(', ') || '—'}` },
      { id: 'intent', label: `requestId: ${intent?.intentId || '—'}` },
      { id: 'execution', label: `executionHash/tx: ${txHash || '—'}` },
      { id: 'cid', label: `ipfsCid: ${intent?.intentCid || execution?.executionCid || '—'}` }
    ];
    const requester = intent.requesterEmail || intent.requestedByEmail || intent.requester || intent.createdByEmail || 'Requestor';
    const summary = `Deployment request by ${requester}. ${policyIntent}. Current state: ${overallStatus.replace(/_/g, ' ').toLowerCase()}.`;

    setDrawerData({
      summary,
      events: timelineEvents,
      audit: auditItems,
      rawJson: { intent, policy, approvals, execution, derived },
      copyItems: [
        { label: 'Pipeline URL', value: intent?.pipelineUrl || '' },
        { label: 'Request ID', value: intent?.intentId || '' },
        { label: 'Tx Hash', value: txHash || '' }
      ]
    });
  }, [setDrawerData, intent, policy, approvals, execution, derived, timelineItems]);

  return (
    <div className="release-details">
      <div className="release-header">
        <div>
          <h3>{`Deploy to ${intent.environment || 'Target'}`}</h3>
          <div className="release-subtitle">
            <span
              title={intent.commitHash || ''}
            >
              {intent.pipelineId ? `Pipeline #${intent.pipelineId}` : 'Pipeline'} • Commit on record
            </span>
            <div className="muted">Process: {humanizeProcessType(intent.processType || intent.process_type || 'RELEASE_INTENT_V0')}</div>
          </div>
        </div>
        <StatusPill overallStatus={overallStatus} />
      </div>
      <VerdictBanner label={verdict.label} tone={verdict.tone} />

      <div className="release-toggle">
        <button
          type="button"
          className={isEngineer ? 'active' : ''}
          onClick={() => onViewChange('engineer')}
        >
          Operational View
        </button>
        <button
          type="button"
          className={!isEngineer ? 'active' : ''}
          onClick={() => onViewChange('auditor')}
        >
          Compliance View
        </button>
      </div>

      {isEngineer ? (
        <>
          <section className="release-section">
            <h4>Timeline</h4>
            <Timeline items={timelineItems} />
          </section>

          <div className="release-actions">
            {intent.pipelineUrl ? (
              <a className="connect-button" href={intent.pipelineUrl} target="_blank" rel="noreferrer">
                GitLab Pipeline
              </a>
            ) : (
              <button type="button" className="connect-button" disabled>
                Pipeline link unavailable
              </button>
            )}
            <button
              type="button"
              className="connect-button secondary"
              onClick={() => onViewChange('auditor')}
            >
              View Audit Record
            </button>
          </div>

          <ExpandableSection title="Approvals">
            <p className="muted">{policyLabel}</p>
            <p className="section-label">Approvals Required</p>
            <ApprovalsList requiredRows={requiredRows} roles={roles} />
          </ExpandableSection>

          <ExpandableSection title="Execution">
            {overallStatus === 'PENDING_APPROVAL' && (
              <p>Deployment not executed — waiting for required approvals.</p>
            )}
            {overallStatus === 'APPROVED_PENDING_EXECUTION' && (
              <p>Deployment not executed — approvals satisfied; awaiting execution record.</p>
            )}
            {overallStatus === 'EXECUTED_SUCCESS' && (
              <p>Deployment executed (Succeeded) at {formatDateTimeOrDash(execution?.executedAt)}.</p>
            )}
            {overallStatus === 'EXECUTED_FAILED' && (
              <p>Deployment executed (Failed). {execution?.errorSummary || ''}</p>
            )}
          </ExpandableSection>

          <ExpandableSection title="Evidence">
            <p className="muted">
              Evidence records are available for this deployment request.
            </p>
            <button
              type="button"
              className="link-toggle"
              onClick={() => openDrawer('summary')}
            >
              View technical details
            </button>
          </ExpandableSection>
        </>
      ) : (
        <>
          <section className="release-section">
            <h4>Timeline</h4>
            <Timeline items={timelineItems} />
          </section>

          <section className="release-section">
            <h4>Decision Snapshot</h4>
            <div className="decision-snapshot">
              <div className="decision-row">
                <div>
                  <span className="summary-label">Authorization</span>
                  <div className="decision-value">
                    <StatusBadge status={derived.approvalsSatisfied ? 'Approved' : 'Not approved'} tone={authorizationTone} />
                    <span className="summary-sub">
                      {derived.approvalsSatisfied ? 'Policy satisfied' : 'Approvals missing'}
                    </span>
                  </div>
                </div>
                <div>
                  <span className="summary-label">Execution</span>
                  <div className="decision-value">
                    <StatusBadge status={execution?.status && execution.status !== 'NOT_EXECUTED' ? 'Recorded' : 'Not recorded'} tone={executionTone} />
                    <span className="summary-sub">{executionSummary}</span>
                  </div>
                </div>
              </div>
              <div className="decision-row">
                <div>
                  <span className="summary-label">Policy</span>
                  <div className="decision-value">
                    <span>{policyLabel}</span>
                  </div>
                  <span className="summary-sub">{policyIntent}</span>
                </div>
                <div>
                  <span className="summary-label">Expires</span>
                  <div className="decision-value">
                    <span>{formatDateTimeOrDash(intent.expiresAt)}</span>
                  </div>
                  <span className="summary-sub">{expiryCountdown || expiryNote}</span>
                </div>
              </div>
            </div>
            <p className="muted decision-note">{complianceSentence}</p>
          </section>

          <ExpandableSection title="Approvers">
            <ApprovalsTable requiredRows={requiredRows} roles={roles} />
          </ExpandableSection>

          <ExpandableSection title="Evidence">
            <p className="muted">
              Evidence records are available for this deployment request.
            </p>
            <button
              type="button"
              className="link-toggle"
              onClick={() => openDrawer('summary')}
            >
              View technical details
            </button>
          </ExpandableSection>

          <section className="release-section">
            <h4>Verification</h4>
            <VerificationChecklist verification={derived.verification} />
            {derived.verification?.cidLastCheckedAt && (
              <p className="muted">
                CID check: {derived.verification.cidCheckMethod || 'gateway'} at {formatDateTimeOrDash(derived.verification.cidLastCheckedAt)}
              </p>
            )}
            {derived.verification?.cidCheckError && (
              <p className="error-text">CID check error: {derived.verification.cidCheckError}</p>
            )}
          </section>
        </>
      )}
    </div>
  );
};

export default ReleaseDetails;
